import { ChoiceCard, RadioDot } from '@/components/onboarding/choice-card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRegions } from '@/hooks/use-regions';

/**
 * Data-region choice cards. The list comes from `GET /regions`, so only
 * deployed regions are offered, and each card has a one-line `note` slot that
 * stays empty until the wording is approved. Never pre-selected from
 * location: this is the buyer's deliberate choice.
 */
export function RegionPicker({
    region,
    onRegionChange,
}: {
    region: string;
    onRegionChange: (code: string) => void;
}) {
    const { data, isPending, isError, refetch } = useRegions();
    const regions = data?.items ?? [];

    if (isPending) {
        return (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="regions-loading">
                <Skeleton className="h-[72px] w-full rounded-xl" />
                <Skeleton className="h-[72px] w-full rounded-xl" />
            </div>
        );
    }

    if (isError) {
        // A failed region list is a failure, never an empty picker: the server
        // answers 503 `regions_unavailable` rather than an empty list precisely
        // so this cannot be mistaken for "no regions exist".
        return (
            <div className="rounded-xl border border-destructive/40 p-4 text-sm">
                <p className="text-destructive">The list of regions could not be loaded.</p>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => void refetch()}
                >
                    Try again
                </Button>
            </div>
        );
    }

    return (
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Data region">
            {regions.map((r) => {
                const on = region === r.code;
                return (
                    <ChoiceCard
                        key={r.code}
                        selected={on}
                        onSelect={() => onRegionChange(r.code)}
                        className="px-4 py-3.5"
                    >
                        <div className="flex items-start gap-3">
                            <RadioDot on={on} />
                            <span className="text-lg leading-none" aria-hidden>
                                {r.flag}
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-[15px] font-medium tracking-[-0.01em] text-foreground">
                                    {r.name}
                                </div>
                                {r.note && (
                                    <div className="mt-0.5 text-[13px] leading-snug text-muted-foreground">
                                        {r.note}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ChoiceCard>
                );
            })}
        </div>
    );
}
